import api from './api';

class TokenMetadataService {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
  }

  // Get token metadata (symbol, name, logo) from mint address
  async getTokenMetadata(mintAddress) {
    if (!mintAddress) return null;
    
    // Check cache first
    if (this.cache.has(mintAddress)) {
      return this.cache.get(mintAddress);
    }

    // Check if we already have a pending request for this token
    if (this.pendingRequests.has(mintAddress)) {
      return this.pendingRequests.get(mintAddress);
    }

    // Create new request
    const request = this.fetchTokenMetadata(mintAddress);
    this.pendingRequests.set(mintAddress, request);

    try {
      const metadata = await request;
      this.cache.set(mintAddress, metadata);
      this.pendingRequests.delete(mintAddress);
      return metadata;
    } catch (error) {
      this.pendingRequests.delete(mintAddress);
      // Cache the error result to avoid repeated failed requests
      const fallback = {
        symbol: mintAddress.slice(0, 4).toUpperCase(),
        name: 'Unknown Token',
        mint: mintAddress
      };
      this.cache.set(mintAddress, fallback);
      return fallback;
    }
  }

  async fetchTokenMetadata(mintAddress) {
    try {
      console.log(`[TokenMetadata] Fetching metadata for ${mintAddress}`);
      
      // Try our backend endpoint first
      const response = await api.post('/wallet/token-metadata', {
        mintAddress
      });
      
      if (response.data.success) {
        console.log(`[TokenMetadata] Found metadata for ${mintAddress}:`, response.data.symbol);
        return {
          symbol: response.data.symbol || mintAddress.slice(0, 4).toUpperCase(),
          name: response.data.name || 'Unknown Token',
          mint: mintAddress,
          logo: response.data.logo,
          decimals: response.data.decimals
        };
      }
    } catch (error) {
      console.warn(`[TokenMetadata] Failed to fetch metadata for ${mintAddress}:`, error);
    }

    // Fallback to abbreviated mint address
    console.log(`[TokenMetadata] Using fallback for ${mintAddress}`);
    return {
      symbol: mintAddress.slice(0, 4).toUpperCase(),
      name: 'Unknown Token',
      mint: mintAddress
    };
  }

  // Batch get metadata for multiple tokens
  async getBatchTokenMetadata(mintAddresses) {
    const promises = mintAddresses.map(mint => this.getTokenMetadata(mint));
    return Promise.all(promises);
  }

  // Clear cache
  clearCache() {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  // Get cached metadata (synchronous)
  getCachedMetadata(mintAddress) {
    return this.cache.get(mintAddress) || null;
  }
}

export default new TokenMetadataService();